/* eslint-disable */
/**
 * seoConfig.js — the single source of truth for every crawler-facing tag.
 *
 * Deliberately CommonJS: this file is consumed twice.
 *   1. by the React app at runtime (src/seo.js) for client-side navigation, and
 *   2. by Node at build time (scripts/prerender.js) to bake the same tags into
 *      a static HTML file per route, so crawlers that don't execute JS — every
 *      social scraper, and Googlebot on its first pass — still see them.
 *
 * If you add a route to src/App.js, add it here too or it ships with the
 * generic home-page metadata.
 */

// Canonical origin. Override at build time with SITE_URL=... npm run build.
// No trailing slash.
const SITE_URL = (process.env.SITE_URL || process.env.REACT_APP_SITE_URL || 'https://www.omniprac.com')
  .replace(/\/+$/, '');

const SITE_NAME = 'OmniPrac';
const LOCALE = 'en_ZA';
const CONTACT_EMAIL = 'info@omniprac.com';

// Social card. Absolute URL — relative og:image is ignored by most scrapers.
const OG_IMAGE = SITE_URL + '/og-card.png';
const OG_IMAGE_W = 1200;
const OG_IMAGE_H = 630;

/**
 * One entry per route. `path` must match the React Router path exactly.
 *   title       <title> and og:title
 *   description meta description and og:description (aim 120–158 chars)
 *   noindex     true keeps the page out of the index and out of sitemap.xml
 *   priority    sitemap.xml <priority>
 */
const ROUTES = [
  {
    path: '/',
    title: 'OmniPrac — Practice Management Software for Anaesthetists in South Africa',
    description:
      'OmniPrac is practice management software built for anaesthetists: cross-hospital scheduling, ICD-10 coding, time-based billing with automatic modifiers, claims and collections.',
    priority: '1.0',
    changefreq: 'weekly',
  },
  {
    path: '/pricing',
    title: 'Pricing — OmniPrac Anaesthetic Practice Management',
    description:
      'OmniPrac is priced per practice by quotation, scaled to your number of anaesthetists, hospitals and modules. Request a tailored quote — no obligation.',
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/login',
    title: 'Client Login — OmniPrac',
    description: 'Sign in to OmniPrac. Enter your practice name to be taken to your practice login page.',
    noindex: true,
  },
  {
    path: '/practice',
    title: 'Client Login — OmniPrac',
    description: 'Sign in to OmniPrac. Enter your practice name to be taken to your practice login page.',
    noindex: true,
  },
  {
    path: '/404',
    title: 'Page not found — OmniPrac',
    description: 'That page does not exist. Head back to the OmniPrac home page.',
    noindex: true,
  },
];

const DEFAULT_ROUTE = ROUTES[0];
const NOT_FOUND_ROUTE = ROUTES.find((r) => r.path === '/404');

/**
 * Look a route up by pathname, tolerating a trailing slash. An unknown path
 * resolves to the noindex 404 entry, never to the home page — otherwise every
 * junk URL would advertise itself to Google as a copy of the home page.
 */
function getRoute(pathname) {
  const clean = (pathname || '/').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  return ROUTES.find((r) => r.path === clean) || NOT_FOUND_ROUTE;
}

/** Absolute canonical URL for a route path. */
function canonicalFor(path) {
  return path === '/' ? SITE_URL + '/' : SITE_URL + path;
}

/**
 * Organization structured data. THIS is what lets Google associate the
 * OmniPrac logo with the brand in search results and the knowledge panel:
 * `logo` must be an absolute URL to a crawlable raster at least 112x112px.
 * Keep it in sync with public/logo-org.png (regenerate via scripts/make-icons.ps1).
 */
function organizationSchema() {
  return {
    '@type': 'Organization',
    '@id': SITE_URL + '/#organization',
    name: SITE_NAME,
    alternateName: 'OmniPrac Practice Management',
    url: SITE_URL + '/',
    logo: {
      '@type': 'ImageObject',
      '@id': SITE_URL + '/#logo',
      url: SITE_URL + '/logo-org.png',
      contentUrl: SITE_URL + '/logo-org.png',
      width: 1320,
      height: 400,
      caption: SITE_NAME,
    },
    image: { '@id': SITE_URL + '/#logo' },
    description:
      'Practice management software for anaesthetists — scheduling, ICD-10 coding, time-based billing, claims and collections.',
    email: CONTACT_EMAIL,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ZA',
    },
    areaServed: {
      '@type': 'Country',
      name: 'South Africa',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: CONTACT_EMAIL,
        areaServed: 'ZA',
        availableLanguage: ['en'],
      },
    ],
    // Add real profiles here as they go live — sameAs is a strong signal for
    // entity matching, and an empty array is better than a fabricated one.
    sameAs: [],
  };
}

/** WebSite node — gives Google the site name to display under the result. */
function websiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': SITE_URL + '/#website',
    url: SITE_URL + '/',
    name: SITE_NAME,
    inLanguage: 'en-ZA',
    publisher: { '@id': SITE_URL + '/#organization' },
  };
}

/** The product itself, so the software can rank as an entity in its own right. */
function softwareSchema() {
  return {
    '@type': 'SoftwareApplication',
    '@id': SITE_URL + '/#software',
    name: 'OmniPrac',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Medical practice management software',
    operatingSystem: 'Web browser',
    url: SITE_URL + '/',
    image: SITE_URL + '/og-card.png',
    description:
      'Practice management software for anaesthetists: cross-hospital scheduling, pre-op risk, ICD-10 coding, time-based billing with automatic BMI, emergency, after-hours and neonate modifiers, switch-integrated claims and collections.',
    publisher: { '@id': SITE_URL + '/#organization' },
    // No `offers` node on purpose: OmniPrac is quoted per practice, and a
    // placeholder price (or price "0") would be published to Google as fact.
    featureList: [
      'Time-based anaesthetic billing engine',
      'Automatic BMI, emergency, after-hours and neonate modifiers',
      'Cross-hospital theatre scheduling',
      'ICD-10 coding',
      'Clearinghouse switch integration for claims',
      'Collections and claim status tracking',
      'POPIA-aligned, role-based access with audit trail',
    ],
  };
}

/** The @graph shipped on every page. */
function siteSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [organizationSchema(), websiteSchema(), softwareSchema()],
  };
}

module.exports = {
  SITE_URL,
  SITE_NAME,
  LOCALE,
  CONTACT_EMAIL,
  OG_IMAGE,
  OG_IMAGE_W,
  OG_IMAGE_H,
  ROUTES,
  DEFAULT_ROUTE,
  NOT_FOUND_ROUTE,
  getRoute,
  canonicalFor,
  siteSchema,
  organizationSchema,
  websiteSchema,
  softwareSchema,
};
